import { Injectable } from '@angular/core';
import { EventResponse, IdNameType, TelemetryResponse, TripRating, TripResponse } from '@key-telematics/fleet-api-client';
import { TranslateService } from '@ngx-translate/core';
import { AppService } from 'app/app.service';
import { EventFeedItem } from 'app/shared/components/feed/feed.model';
import { LabelBadge } from 'app/shared/components/labels/label-badges.component';
import { getTimeBuckets } from 'app/shared/utils/time-buckets';
import * as Bluebird from 'bluebird';
import * as moment from 'moment-timezone';
import { defer, filter, first, from, lastValueFrom, repeat } from 'rxjs';
import { AssetStateService } from '../assets/asset-state.service';

export interface TripLinkedAsset extends IdNameType {
    icon?: { name: string, color: string };
}

export interface TripArgs {
    assetId: string;
    dateStart: string;
    dateEnd: string;
    telemetry?: TelemetryResponse[];
    events?: EventFeedItem[];
    hidden?: boolean;
    private?: boolean;
    hasTrips?: boolean;
}

export interface TripItem extends TripArgs {
    id: string;
    tripType: 'inactive' | 'active' | 'mixed';
    lonStart: number;
    latStart: number;
    addressStart: string;
    lonEnd: number;
    latEnd: number;
    addressEnd: string;
    driveTime: number;
    idleTime: number;
    distance: number;
    duration: number;
    rating?: TripRating;
    linkedAssets?: TripLinkedAsset[];
    inprogress?: boolean;
    labels?: LabelBadge[];
    private?: boolean;
}

export function getTripId(trip: Pick<TripArgs, 'dateStart'>): string {
    return moment.utc(trip.dateStart).format('DDHHmmss');
}

export function tripMatchesId(trip: TripArgs, id: string): boolean {
    return getTripId(trip) === id;
}

@Injectable()
export class TripsService {
    private promiseCache: { [key: string]: Promise<any> } = {};

    constructor(
        public app: AppService,
        private i18n: TranslateService,
        private assetState: AssetStateService
    ) {
    }

    mapTrip(x: TripResponse): TripItem {
        const result = {
            id: x.id,
            assetId: x.asset.id,
            tripType: x.tripType,
            dateStart: x.dateStart,
            lonStart: x.start && x.start.lon,
            latStart: x.start && x.start.lat,
            addressStart: (x.start && x.start.address) || this.i18n.instant('SHARED.UNKNOWN_LOCATION'),
            dateEnd: x.dateEnd,
            latEnd: x.end && x.end.lat,
            lonEnd: x.end && x.end.lon,
            addressEnd: (x.end && x.end.address) || this.i18n.instant('SHARED.UNKNOWN_LOCATION'),
            rating: x.rating,
            driveTime: (x.stats && x.stats.driveTime) || 0,
            idleTime: (x.stats && x.stats.idleTime) || 0,
            distance: (x.stats && x.stats.distance) || 0,
            duration: this.getTripDurationSeconds(x.dateStart, x.dateEnd),
            linkedAssets: x.linkedAssets,
            inprogress: false,
            labels: x.labels || [],
            private: x.private || false,
        };
        return result;
    }


    async loadTrips(assetId: string, start: string, end: string): Promise<TripItem[]> {
        this.clearCache();
        const limit = this.app.features.page.overview.pageSize || 300;

        // 'in-progress' trips are only displayed if the user has visited the overview page
        // if they have not or the app has been refreshed since then we need initialize assets
        if (this.assetState.assets?.length === 0) {
            await this.assetState.initialize(0, this.app.client.id, limit, 0, 'asc', '' );
        }
        const trips = await this.app.api.data.getTripHistory(assetId, start, end);
        return trips.items.map(x => this.mapTrip(x));
    }

    loadTripTelemetry(trip: TripArgs): PromiseLike<TelemetryResponse[]> {
        return Bluebird.map(
            getTimeBuckets(trip.dateStart, trip.dateEnd, 24 * 60 ** 2),
            (dates) => {
                return this.loadSingleTripTelemetry({
                    ...trip,
                    dateStart: dates[0],
                    dateEnd: dates[1],
                });
            },
            {
                concurrency: 1,
            }
        )
            .then(telemetries => telemetries.reduce((all, current) => all.concat(current), []));
    }

    async loadSingleTripTelemetry(trip: TripArgs): Promise<TelemetryResponse[]> {
        if (trip?.private) {
            // private trips have no telemetry ;)
            return [];
        }

        // We expect the response to have some telemetry, keep retrying until it does.
        // If we still can't find any after trying a specified number of times, then we return the API response as a default
        const getTelemetryHistory = (repeatCount: number) => lastValueFrom(
            defer(() => from(this.app.api.data.getTelemetryHistory(trip.assetId, trip.dateStart, trip.dateEnd)))
                .pipe(
                    repeat({ count: repeatCount, delay: 1000 }),
                    filter((res, index) => res?.items?.length > 0 || index === repeatCount - 1),
                    first()
                ));

        const key = `getTelemetryHistory/${trip.assetId}/${trip.dateStart}/${trip.dateEnd}`;
        

        // If this trip belongs to an asset that doesn't have trips, it's more likely that we'll come across entries without telemetry. 
        // In this situation we don't want to keep retrying as it'll take ages to load a large date range 
        // This is also stupid
        const telemetryHistoryPromise = trip?.hasTrips === false ? getTelemetryHistory(1) : getTelemetryHistory(10);
        this.promiseCache[key] = this.promiseCache[key] || telemetryHistoryPromise;
        const res = await this.promiseCache[key];
        return res.items;
    }

    loadTripEvents(trip: TripArgs): PromiseLike<EventResponse[]> {
        return Bluebird.map(
            getTimeBuckets(trip.dateStart, trip.dateEnd, 24 * 60 ** 2),
            dates => {
                return this.loadSingleTripEvents({
                    ...trip,
                    dateStart: dates[0],
                    dateEnd: dates[1],
                });
            },
            {
                concurrency: 1,
            }
        ).then(telemetries =>
            telemetries.reduce((all, current) => all.concat(current), [])
        );
    }

    loadSingleTripEvents(trip: TripArgs): Promise<EventResponse[]> {
        const key = `getEventHistory/${trip.assetId}/${trip.dateStart}/${trip.dateEnd}`;
        this.promiseCache[key] = this.promiseCache[key] || this.app.api.data.getEventHistory(trip.assetId, trip.dateStart, trip.dateEnd);
        return this.promiseCache[key].then(res => {
            return res.items;
        });
    }

    getTripDurationSeconds(start: string, end: string): number {
        const s = moment(start);
        const e = moment(end);
        return e.diff(s) / 1000;
    }

    clearCache() {
        this.promiseCache = {};
    }
}
